import React, { useState, useRef, useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import publicAxios from "../../utils/publicAxios";
import { showAlert, showToast } from "../../utils/utils";
import {
  checklistQuestion,
  responseStatus,
  commonValidationMsg,
  alertActions
} from "../../locales/en";
import { FaRegTrashAlt } from "react-icons/fa";
import HeaderDashboard from "../../components/layout/header/HeaderDashboard";
import "./../../assets/scss/home.scss";
import "./checklistquestion.scss";
import { FILE_SIZE_LIMIT } from "../../utils/constants/appConstants";
import {
  getQuestionResponsesQueryForFundingUploadURL,
  getQuestionResponsesQueryForFundingURL
} from "../../utils/constants/apiEndPoints";

function ChecklistQuestions() {
  const [fileNames, setFileNames] = useState({});
  const [fileObjects, setFileObjects] = useState({});
  const [answer, setAnswer] = useState({});
  const [sendAnswerData, setSendAnswerData] = useState([]);
  const fileInputs = useRef({});
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const getToken = queryParams.get("token");

  const fetchQAData = useCallback(async () => {
    try {
      const response = await publicAxios.get(
        getQuestionResponsesQueryForFundingURL,
        {
          params: {
            token: getToken
          }
        }
      );
      setSendAnswerData(response?.data?.data);
    } catch {
      // for error message
    } finally {
      // for finally return
    }
  }, []);

  useEffect(() => {
    fetchQAData();
  }, [fetchQAData]);

  const triggerFileDownload = (filePath, fileName) => {
    if (filePath && fileName) {
      const link = document.createElement("a");
      link.href = filePath;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleFileChange = (event, itemId) => {
    const { files } = event.target;
    if (!files || files.length === 0) return;

    const validFiles = Array.from(files).filter((file) => {
      if (file.size > FILE_SIZE_LIMIT) {
        showToast(commonValidationMsg?.fileLimitError, responseStatus?.error);
        return false;
      }
      const alreadyExists = fileObjects[itemId]?.some(
        (existingFile) => existingFile.name === file.name
      );
      if (alreadyExists) return false;
      return true;
    });

    if (validFiles.length === 0) return;

    setFileNames((prev) => ({
      ...prev,
      [itemId]: [
        ...(prev[itemId] || []),
        ...validFiles.map((file) => file.name)
      ]
    }));
    setFileObjects((prev) => ({
      ...prev,
      [itemId]: [...(prev[itemId] || []), ...validFiles]
    }));
    event.target.value = null;
  };

  const handleDeleteFile = (itemId, fileNameToDelete) => {
    setFileNames((prev) => ({
      ...prev,
      [itemId]: prev[itemId].filter((fileName) => fileName !== fileNameToDelete)
    }));

    setFileObjects((prev) => ({
      ...prev,
      [itemId]: prev[itemId]?.filter((file) => file.name !== fileNameToDelete)
    }));

    if (fileInputs.current[itemId]) {
      fileInputs.current[itemId].value = null;
    }
  };

  const handleUploadButtonClick = (itemId) => {
    if (fileInputs.current[itemId]) {
      fileInputs.current[itemId].click();
    }
  };

  const handleInputChange = (e, itemId) => {
    const { value } = e.target;
    setAnswer((prev) => ({
      ...prev,
      [itemId]: value
    }));
  };

  const handleSubmit = async () => {
    const formData = new FormData();

    sendAnswerData.forEach((item, index) => {
      const itemId = item.id;

      formData.append(`checklistQuestionResponsesDtos[${index}].id`, itemId);
      formData.append(
        `checklistQuestionResponsesDtos[${index}].answerText`,
        answer[itemId] || ""
      );

      const files = fileObjects[itemId] || [];
      files.forEach((file) => {
        formData.append(`checklistQuestionResponsesDtos[${index}].file`, file);
      });
    });

    try {
      const response = await publicAxios.put(
        getQuestionResponsesQueryForFundingUploadURL + getToken,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        }
      );
      fetchQAData();
      await showAlert(response?.data?.message, "", "", alertActions?.ok);

      setAnswer({});
      setFileNames({});
      setFileObjects({});

      Object.values(fileInputs.current).forEach((input) => {
        if (input) input.value = null;
      });
    } catch (error) {
      showToast(error.response.data.message, responseStatus?.error);
    }
  };

  return (
    <>
      <HeaderDashboard />

      <div className="home-main-section">
        <div className="container">
          <div className="home-title">
            <h1>{checklistQuestion?.title}</h1>
          </div>
        </div>
      </div>

      <div className="question-answer-section mt-5 ">
        <div className="container">
          <div className="question-title-wrap">
            <p>
              <strong>{checklistQuestion?.subH2}</strong>
            </p>
          </div>
          <div className="question-ans-wrap">
            <table>
              <tbody>
                {sendAnswerData?.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <div className="d-flex align-items-start justify-content-between gap-2 w-100">
                        <div className="d-flex align-items-start gap-2">
                          <div className="mb-10">
                            <strong>
                              {checklistQuestion?.listData?.question}
                            </strong>
                            {item?.question}
                          </div>
                        </div>
                        <div className="d-flex flex-column gap-1">
                          {Array.isArray(item?.download) &&
                            item.download.length > 0 &&
                            item.download.map((file) => (
                              <div
                                key={file.filePath}
                                className="d-flex align-items-center justify-content-between text-truncate"
                              >
                                <button
                                  type="button"
                                  className="btn btn-link p-0 text-primary text-decoration-underline"
                                  onClick={() =>
                                    triggerFileDownload(
                                      file.filePath,
                                      file.fileName
                                    )
                                  }
                                >
                                  {file?.fileName}
                                </button>
                              </div>
                            ))}
                        </div>
                      </div>
                      <div className="mt-3 mb-3">
                        <strong>{checklistQuestion?.listData?.response}</strong>
                        <textarea
                          type="text"
                          className="form-control mb-3"
                          rows={1}
                          value={answer[item.id] || ""}
                          onChange={(e) => handleInputChange(e, item.id)}
                        />
                      </div>
                      <div className="mt-3 mb-3">
                        <strong>
                          {checklistQuestion?.listData?.uploadLabel}
                        </strong>
                        <br />
                        <button
                          type="button"
                          onClick={() => handleUploadButtonClick(item.id)}
                          className="btn btn-primary fundtap_agreement"
                        >
                          {checklistQuestion?.listData?.uploadBtn}
                        </button>
                        <input
                          name="isFile"
                          type="file"
                          multiple
                          ref={(el) => (fileInputs.current[item.id] = el)}
                          onChange={(e) => handleFileChange(e, item.id)}
                          className="d-none"
                        />
                        {fileNames[item.id]?.length > 0 && (
                          <div className="uploaded-files-list">
                            {fileNames[item.id].map((fileName, index) => (
                              <div
                                key={`${fileName}-${index}`}
                                className="d-flex align-items-center uploaded-file-name mb-1"
                              >
                                <span className="text-truncate me-2">
                                  {fileName}
                                </span>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-danger"
                                  onClick={() =>
                                    handleDeleteFile(item.id, fileName)
                                  }
                                >
                                  <FaRegTrashAlt />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {sendAnswerData?.length > 0 && (
            <div className="ans-submit mt-3">
              <button className="btn btn-primary" onClick={handleSubmit}>
                {checklistQuestion?.listData?.submitBtn}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
export default ChecklistQuestions;
